import React, { useState } from 'react';
import { DOMAIN_CONFIG } from '../config';
import { ChevronDown, HelpCircle } from 'lucide-react';

interface FAQItem {
  question: string;
  answer: string;
}

const faqs: FAQItem[] = [
  {
    question: `How fast is the transfer process for ${DOMAIN_CONFIG.domainName}?`,
    answer: `Once terms are agreed upon and payment is funded in escrow, transfer usually takes less than 24-48 hours. If you use the same registrar for a direct account push, the handover can be completed in under 30 minutes.`,
  },
  {
    question: 'How does escrow protect me as the buyer?',
    answer: 'Using an accredited escrow agent like Escrow.com or Sedo ensures that your payment is held securely in trust. The seller does not receive a single dollar until you have received the domain authorization code, transferred it to your registrar, and confirmed full administrative control.',
  },
  {
    question: 'Are there hidden brokerage fees or commissions?',
    answer: `No. You are dealing directly with the domain asset owner. Standard escrow transaction fees (typically split or covered based on agreed offer amount) are fully transparent with no hidden commissions.`,
  },
  {
    question: 'Are lease-to-own or milestone payment terms available?',
    answer: 'Yes, for verified corporate buyers, we are open to structuring a lease-to-own or milestone agreement via Dan.com or Escrow.com Domain Holding service.',
  },
  {
    question: `Is the domain owner obligated to sell?`,
    answer: `No. This website is currently published to gauge market interest, potential use cases, and indicative valuations for ${DOMAIN_CONFIG.domainName}. All inquiries and offers submitted through this page are non-binding expressions of interest. The domain owner reserves full discretion and the right to accept, decline, counter any proposal, or decide not to sell the domain.`,
  },
  {
    question: 'What are the annual renewal fees for a .dev domain?',
    answer: 'Unlike some exotic registries, .dev domains carry standard low annual renewals (typically around $12 - $15 per year) across major registrars such as Porkbun, Namecheap, and GoDaddy.',
  },
];

export const FAQ: React.FC = () => {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  const toggle = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <section id="faq" className="py-20 md:py-28 relative bg-[#060a16]/40">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-2xl mx-auto mb-16">
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-slate-900 border border-slate-700/80 text-emerald-400 text-xs font-mono mb-4">
            <HelpCircle className="w-3.5 h-3.5" />
            <span>TRANSACTION QUESTIONS</span>
          </div>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-white tracking-tight">
            Frequently Asked Questions
          </h2>
          <p className="mt-4 text-base text-slate-300">
            Everything you need to know about the acquisition, escrow verification, and transfer process.
          </p>
        </div>

        <div className="space-y-4">
          {faqs.map((faq, index) => {
            const isOpen = openIndex === index;
            return (
              <div
                key={faq.question}
                className="glass-panel rounded-2xl border border-slate-800/80 overflow-hidden transition-colors"
              >
                <button
                  onClick={() => toggle(index)}
                  className="w-full px-6 py-5 text-left flex items-center justify-between gap-4 focus:outline-none"
                >
                  <span className="font-semibold text-base sm:text-lg text-white">
                    {faq.question}
                  </span>
                  <div
                    className={`w-7 h-7 rounded-lg bg-slate-800/80 border border-slate-700 flex items-center justify-center text-slate-400 shrink-0 transition-transform duration-200 ${
                      isOpen ? 'rotate-180 text-emerald-400 border-emerald-500/40' : ''
                    }`}
                  >
                    <ChevronDown className="w-4 h-4" />
                  </div>
                </button>

                {isOpen && (
                  <div className="px-6 pb-6 pt-1 text-slate-300 text-sm sm:text-base leading-relaxed border-t border-slate-800/60 animate-in fade-in duration-200">
                    {faq.answer}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
